import {Observable} from 'rxjs';
import {DataSource} from '@mominsamir/ngx-smart-table/lib/lib/data-source/data-source';
import {
    APIResponse,
    Filter,
    SimpleData,
    SimpleMultiSeries,
    SimpleSeries,
    ThresholdStatic,
} from '@core/interfaces/system/system-common';
import {GuideInfo} from '@theme/components';
import {LiveConfig} from '@core/interfaces/common/configuration';

/*
 * Request DTOs
 */
export interface LiveModelRequest {
    assetId?: string;
    model?: string;
    uploadTime?: string;
}

export interface LiveAssetRequest {
    assetId: string;
    uploadTime: string;
}

export interface LiveFleetRequest {
    uploadTime: string;
}

/*
 * Response DTOs
 */
export interface LatestModelStatus {
    modelStatusList: AlertStatusEnum[];
    status: AlertStatusEnum;
    faultType: string;
    assetInfo: InfoItem[];
    modelInfo?: InfoItem[];
}
export interface ChartModelHistoryResponse {
    data: ModelHistory[];
    statusList: AlertStatusEnum[];
}
export interface ModelHistory {
    date: string;
    alertStatus: AlertStatusEnum;
    alertStatusLabel?: string;
}

export interface ChartModelAlertHistoryResponse {
    rangeFilter: string[];
    data: SimpleData<AggregateAlert>;
    statusList: AlertStatusEnum[];
}
export interface AggregateAlert {
    status: AlertStatusEnum;
    date: Date;
    count: number;
}

export interface ChartAssetAlertHistoryResponse extends Alert {}

export interface FleetLatestStatusResponse {
    totalAssets: {current: number; previous: number; change: number};
    totalAlerts: {current: number; previous: number; change: number};
    data: SystemAlertCount[];
}

export interface RowDataReadingsDashboardData {
    data: SimpleMultiSeries<Date>;
}
export interface FaultIndicationDashboardData {
    data: SimpleSeries<Date, number>;
    thresholds: ThresholdStatic[];
}

/*
 * Supporting DTOs
 */
export interface SystemAlertCount {
    status: string;
    alertCount: number;
    assetCount: number;
    previousAlertCount: number;
    alertChange: number;
}

export interface InfoItem {
    label: string;
    value: string;
}

/*
 * Common DTOs
 */
export interface Alert {
    date: string;
    alertStatuses: {
        alertStatusLabel?: string;
        alertStatus: AlertStatusEnum;
        count: number;
    }[];
}

/*
 * Enumerations and labels
 */
// Alert status
export enum AlertStatusEnum {
    None = 'NONE',
    Information = 'INFORMATION',
    Reactive_Priority5 = 'REACTIVE_PRIORITY5',
    Reactive_Priority4 = 'REACTIVE_PRIORITY4',
    Reactive_Priority3 = 'REACTIVE_PRIORITY3',
    Reactive_Priority2 = 'REACTIVE_PRIORITY2',
    Reactive_Priority1 = 'REACTIVE_PRIORITY1',
    Predict_1month = 'PREDICT_1MONTH',
    Predict_3month = 'PREDICT_3MONTH',
    Predict_6month = 'PREDICT_6MONTH',
    Predict_1year = 'PREDICT_1YEAR',
    Predict_3year = 'PREDICT_3YEAR',
    Predict_5year = 'PREDICT_5YEAR',
    MONITOR = 'MONITOR',
    CAUTION = 'CAUTION',
    WARNING = 'WARNING',
    CRITICAL = 'CRITICAL',
    DANGER = 'DANGER',
}
// Index / iterator for displaying indicator
export const AlertStatusInd = {
    [AlertStatusEnum.None]: 0,
    [AlertStatusEnum.Information]: 1,
    [AlertStatusEnum.Predict_5year]: 2,
    [AlertStatusEnum.Predict_3year]: 3,
    [AlertStatusEnum.Predict_1year]: 4,
    [AlertStatusEnum.Predict_6month]: 5,
    [AlertStatusEnum.Predict_3month]: 6,
    [AlertStatusEnum.Predict_1month]: 7,
    [AlertStatusEnum.Reactive_Priority5]: 8,
    [AlertStatusEnum.Reactive_Priority4]: 9,
    [AlertStatusEnum.Reactive_Priority3]: 10,
    [AlertStatusEnum.Reactive_Priority2]: 11,
    [AlertStatusEnum.Reactive_Priority1]: 12,
    [AlertStatusEnum.MONITOR]: 13,
    [AlertStatusEnum.CAUTION]: 14,
    [AlertStatusEnum.WARNING]: 15,
    [AlertStatusEnum.CRITICAL]: 16,
    [AlertStatusEnum.DANGER]: 17,
};
// Model type
export enum ApmModelType {
    SF6_GAS_LEAK = 'SF6_GAS_LEAK',
    HYDROGEN_TRENDING = 'HYDROGEN_TRENDING',
    OIL_TEMP_MOISTURE = 'OIL_TEMP_MOISTURE',
    INSULATION_MOISTURE = 'INSULATION_MOISTURE',
    CO2_LOAD = 'CO2_LOAD',
    ONLINE_DGA = 'ONLINE_DGA',
    DEGREE_OF_POLYMERIZATION = 'DEGREE_OF_POLYMERIZATION',
    THICKNESS_RATE = 'THICKNESS_RATE',
    BATTERY_BANK_ANALYSIS = 'BATTERY_BANK_ANALYSIS',
    VEGETATION_CLEARANCE = 'VEGETATION_CLEARANCE',
}
export const ApmModelTypeLabel = {
    [ApmModelType.SF6_GAS_LEAK]: 'SF6 Gas Leak',
    [ApmModelType.HYDROGEN_TRENDING]: 'Hydrogen Gas Trending',
    [ApmModelType.OIL_TEMP_MOISTURE]: 'Oil Temperature vs. Moisture',
    [ApmModelType.INSULATION_MOISTURE]: 'Insulation Moisture',
    [ApmModelType.CO2_LOAD]: 'CO2 vs Load',
    [ApmModelType.ONLINE_DGA]: 'Dissolved Gas Analysis',
    [ApmModelType.DEGREE_OF_POLYMERIZATION]: 'Degree of Polymerization (DP)',
    [ApmModelType.THICKNESS_RATE]: 'Vessel Thickness',
    [ApmModelType.BATTERY_BANK_ANALYSIS]: 'Battery Bank Analysis',
    [ApmModelType.VEGETATION_CLEARANCE]: 'Vegetation Clearance',
};

// Result type
export enum ApmResultType {
    READING = 'READING',
    MAINTENANCE = 'MAINTENANCE',
    REPLACEMENT = 'REPLACEMENT',
}

export const ApmResultTypeLabels = {
    [ApmResultType.READING]: 'Reading',
    [ApmResultType.MAINTENANCE]: 'Maintenance',
    [ApmResultType.REPLACEMENT]: 'Replacement',
};

export interface AbstractLiveModel {
    alertDescription: string;
    alertStatus: string;
    assetClass: string;
    assetId: string;
    faultType: string;
    id: number;
    model: ApmModelType;
    predicted: boolean;
    readingDate: Date;
    readingTime: Date;
    resultType: string;
    station: string;
    uploadTime: Date;
}

export abstract class LiveService {
    /*
     * Data explorer
     */
    abstract getMeterDataDatasource(): Observable<DataSource>;

    abstract getLiveConfiguration(): Observable<APIResponse<LiveConfig>>;

    /*
     * Generic
     */
    abstract getSearchAssetIds(assetId: string, model: string, asOf: string): Observable<APIResponse<string[]>>;

    abstract getSearchModels(assetId: string, model: string, asOf: string): Observable<APIResponse<string[]>>;

    /*
     * Model, generic
     */
    abstract getCountResultsByModel(request: LiveModelRequest): Observable<number>;

    abstract getChartModelHistory(request: LiveModelRequest): Observable<APIResponse<ChartModelHistoryResponse>>;

    abstract getChartModelAlertHistory(
        request: LiveModelRequest,
        groupBy: string,
    ): Observable<APIResponse<ChartModelAlertHistoryResponse>>;

    /*
     * Asset
     */
    abstract getCountResultsByAsset(assetId: string, uploadTime: string): Observable<number>;

    abstract getChartAssetAlertHistory(
        request: LiveModelRequest,
    ): Observable<APIResponse<ChartAssetAlertHistoryResponse[]>>;

    abstract getAssetLatestModelStatus(id: string, uploadTime: string): Observable<AbstractLiveModel[]>;

    /*
     * Fleet
     */
    abstract getCountResultsByFleet(uploadTime: string): Observable<number>;

    abstract getFleetLatestStatusDetails(request: LiveModelRequest): Observable<APIResponse<FleetLatestStatusResponse>>;

    abstract getCurrentAssetAlerts(request: LiveFleetRequest, filters?: Filter[]): Observable<DataSource>;

    abstract getGuideInfo(modelId: string): Observable<GuideInfo[]>;
}
